/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backbone from 'backbone';

import PageableCollection from '@/backbone/PageableCollection';
import ButtonCell from '@/backgrid/ButtonCell';
import StringCell from '@/backgrid/StringCell';
import GridView from '@/views/GridView';
import showModal from '@/util/showModal';
import lazyLoadView from '@/util/lazyLoadView';
import ConfirmationModalView from '@/views/ConfirmationModalView';

import POCDetailsModalViewLoader from '../../loaders/POCDetailsModalViewLoader';
import UpdateCommandPOC from '../../../external-dashboard/services/UpdateCommandPOC';

const POCGridView = GridView.extend({
  initialize() {
    const viewMode = this.options.viewMode;
    this.pocCollection = new PageableCollection(this.model.get('data'), { pageSize: 5 });

    this.options.columns = [
      {
        name: 'deleteButton',
        label: '',
        width: 5,
        cell: ButtonCell.extend({
          labelClass: 'fa fa-minus text-danger',
          buttonClass: 'btn-sm',
          onClick: this.onClickDeleteCommandPOC.bind(this)
        }),
        editable: false,
        sortable: false,
        renderable: viewMode === 'int-new',
        ariaLabel: 'Delete point of contact'
      },
      {
        name: 'name',
        label: 'Name',
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        editable: false
      },
      {
        name: 'type',
        label: 'Type',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false
      },
      {
        name: 'email',
        label: 'Email',
        cell: StringCell,
        headerCell: 'custom',
        width: 25,
        editable: false
      },
      {
        name: 'telephone',
        label: 'Phone',
        cell: StringCell,
        headerCell: 'custom',
        width: viewMode === 'ext' ? 25 : 35,
        editable: false
      },
      {
        name: 'openButton',
        label: '',
        width: 10,
        cell: ButtonCell.extend({
          name: 'Open',
          buttonClass: 'btn-sm btn-outline-primary',
          onClick: this.viewDetails.bind(this)
        }),
        editable: false,
        sortable: false,
        renderable() {
          return viewMode === 'ext';
        }
      }
    ];
    this.options.collection = this.pocCollection;
    this.options.emptyText = 'No Points of Contact';
  },

  viewDetails(e, model) {
    e.preventDefault();
    const modalData = model.toJSON();
    modalData.edit = true;

    showModal({
      title: 'Edit Point of Contact',
      dialogClasses: 'modal-lg',
      view: lazyLoadView(() => POCDetailsModalViewLoader(this.model.get('commandID'), modalData))
    }).then((formData) => {
      if (formData) {
        _.each(Object.keys(formData), (key) => {
          model.set(key, formData[key]);
        });
      }
    });
  },

  onClickDeleteCommandPOC(e, model) {
    e.preventDefault();
    const contactName = model.get('firstName').concat(' ', model.get('lastName'));
    const type = model.get('type');
    const message = `Are you sure you want to remove ${type} contact ${contactName}?`;

    if (this.options.viewMode === 'int-update') {
      showModal({
        title: 'Confirm Removing Command POC',
        view: new ConfirmationModalView({
          confirmText: 'Remove',
          model: new Backbone.Model({
            message,
            onConfirm: () => {
              const id = model.id;
              return UpdateCommandPOC({ id, isDeleted: '1' });
            },
            onConfirmDone: () => {
              this.pocCollection.remove(model);
            }
          })
        })
      });
    } else {
      this.pocCollection.remove(model);
    }
  },

  getFormData() {
    return this.pocCollection.toJSON();
  },

  toggleDeletion(state) {
    this.getViewGrid().columns.models[0].set('renderable', state);
  }
});

export default POCGridView;
